// @Version: 1.0.5
import { Component, EventEmitter, Output, Input, OnDestroy, forwardRef, signal, OnChanges, SimpleChanges } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormGroup, NG_VALIDATORS, NG_VALUE_ACCESSOR, ValidationErrors, Validator } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { CustomValidators } from './custom-validators';
import { FieldItemRadioService } from './radio.service';
import { FC_RADIO } from './radio.model';
import { DIRECTION, IFieldItem } from '../field-item.model';
import { ErrorMessagesService } from 'src/app/shared/services/errorMessages/error-messages.service';
import { TRANSLATE_PATH } from 'src/app/shared/shared.model';
import { Subscription } from 'rxjs';

@UntilDestroy()
@Component({
   selector: 'app-field-item-radio',
   templateUrl: './radio.component.html',
   providers: [
      {
         provide: NG_VALUE_ACCESSOR,
         useExisting: forwardRef(() => FieldItemRadioComponent),
         multi: true
      },
      {
         provide: NG_VALIDATORS,
         useExisting: forwardRef(() => FieldItemRadioComponent),
         multi: true
      },
      FieldItemRadioService,
      ErrorMessagesService
   ],
   standalone: false
})

export class FieldItemRadioComponent implements OnDestroy, ControlValueAccessor, Validator, OnChanges {

   public TRANSLATE_PATH = TRANSLATE_PATH; 
   public FC_RADIO = FC_RADIO;
   public DIRECTION = DIRECTION;

   public done$$ = signal(false);

   public fgRadio: FormGroup = this.fb.group({});

   private fieldItem_: IFieldItem = {} as IFieldItem;
   private sub_?: Subscription;
   
   @Input() clearValueIfDisabled: boolean = true;

   @Input()
   get fieldItem (): IFieldItem {
      return this.fieldItem_;
   }
   set fieldItem (value: IFieldItem) {
      this.fieldItem_ = value || {};
   }

   @Output() isRequiredMarker: EventEmitter<boolean> = new EventEmitter<boolean>();

   get value (): string {
      return this.fgRadio.get(FC_RADIO.RADIOBUTTON)?.value ?? '';
   }

   constructor (private fb: FormBuilder, public sFieldItemRadio: FieldItemRadioService, private vValidator: CustomValidators) {}
   
   ngOnChanges(changes: SimpleChanges): void {
      if (changes && changes.fieldItem &&  (JSON.stringify(changes.fieldItem.currentValue) != JSON.stringify(changes.fieldItem.previousValue))) {
         this.transformAttribute(changes.fieldItem.currentValue ?? '');
         this.initFormGroup(changes.fieldItem.currentValue);
         this.onValidationChange();
         this.done$$.set(true);
      }
   }

   ngOnDestroy (): void {
      this.reset();
      this.sFieldItemRadio.reset(); 
   }

   validate (control: AbstractControl): ValidationErrors | null {
      this.fgRadio.updateValueAndValidity({emitEvent: false, onlySelf: true});
      return this.fgRadio.valid ? null : this.fgRadio.errors;
   }

   writeValue (value: string | undefined | null): void {
      if (value == undefined) return;
      this.fgRadio.get(FC_RADIO.RADIOBUTTON)?.setValue(value ?? undefined);
      if (typeof value == 'string' && value.trim().length > 0) this.setDirtyTouched();
      this.onValidationChange();
   }

   private setDirtyTouched (): void {
      this.fgRadio.get(FC_RADIO.RADIOBUTTON)?.markAsDirty(); 
      this.fgRadio.get(FC_RADIO.RADIOBUTTON)?.markAsTouched();
   }

   registerOnChange (fn: any): void {
      this.onChange = fn;
   }
   registerOnTouched (fn: any): void {
      this.onTouched = fn;
   }

   private onTouched () { }
   private onChange (_: string) { }

   setDisabledState (isDisabled: boolean | undefined): void { 
      isDisabled ? this.setDisabledControls() : this.fgRadio.enable();
      this.onValidationChange();
   }

   onValidationChange: any = () => {}; 

   registerOnValidatorChange? (fn: () => void): void {
      this.onValidationChange = fn;
   }


   private reset (): void {
      this.done$$.set(false);
      this.fgRadio = this.fb.group({});
      this.fieldItem_ = {} as IFieldItem;
      this.sFieldItemRadio.reset();
   }

   private setDisabledControls (): void {
      this.fgRadio.disable();
      if (this.clearValueIfDisabled) this.fgRadio.get(FC_RADIO.RADIOBUTTON)?.setValue("");
      this.fgRadio.markAsDirty();
   }

   /**
   * Attribut1 = List of Radios
   * @returns 
   */
   private transformAttribute (fieldItem: IFieldItem): boolean {
      if (!fieldItem.attribute_1) return true;
      if (fieldItem.required) this.sFieldItemRadio.required = fieldItem.required;

      this.sFieldItemRadio.createErrorMessages(fieldItem.attribute_2 || '');
      return this.sFieldItemRadio.createRadioButtons(fieldItem.attribute_1 || '')
   }

   private initFormGroup (fieldItem: IFieldItem): boolean {
      if (this.sub_) {
         this.sub_.unsubscribe();
         this.sub_ = undefined;
      }
      this.fgRadio =  this.fb.group({
         [FC_RADIO.RADIOBUTTON]: this.fb.control({value: fieldItem.content, disabled: false}, {updateOn: 'change'})
      });
      if (this.fieldItem.valid) this.fgRadio.addValidators(this.vValidator.isValid(this.fgRadio, this.fieldItem_));
      if (this.fieldItem.required) this.fgRadio.addValidators(this.vValidator.isRequired(this.fgRadio));
   
      this.sub_ = this.fgRadio.valueChanges.pipe( 
      ).subscribe(value => {
         if (!this.value && this.fgRadio.get(FC_RADIO.RADIOBUTTON)?.pristine) return;
         this.onChange(this.value);
         this.onTouched();
      })

      this.isRequiredMarker.emit(fieldItem.required);
      return true;
   }

   public isChecked (value: string): boolean {
      return this.value == value;
   }
}